/*
  Page-level single video player.

  Two sections mount third-party iframes - hero-slideshow and
  editorial-carousel - and each one used to enforce "one at a time" only within
  itself. Playing an editorial card and then landing on a hero video slide left
  two players in the document, one of them unmuted, with neither section aware
  of the other.

  Rather than have either section reach into the other's DOM, both register
  here. A section about to mount a player claims the page; the claim tears down
  whoever held it before. The registry never touches an iframe itself: it only
  calls back the unmount function the owner supplied, so each section keeps
  sole responsibility for its own markup, its own classes and its own poster
  state.

  There is no bundler in this theme, so the instance lives on window. Both
  sections load this file with defer ahead of their own script, and deferred
  scripts execute in document order, so the registry is always defined by the
  time a section's constructor runs.

  Everything is inside an IIFE because both sections request this file, and a
  browser that fetches it once still executes both <script> tags. A top-level
  `class VideoRegistry` is a lexical declaration in global scope, so the second
  execution threw `Identifier 'VideoRegistry' has already been declared` and
  discarded that whole script. Nothing broke, because the first execution had
  already published the instance - it simply logged an uncaught SyntaxError on
  every page carrying both sections. Inside a function scope the second
  execution is a clean no-op, and the class never touches the global namespace.
*/
(function () {
  if (window.themeVideoRegistry) return;

  class VideoRegistry {
    constructor() {
      this.current = null;
    }

    /*
      `token` identifies the individual player, not the section: the editorial
      carousel has one per card, and claiming a card must not tear down the
      card that is already playing if they happen to be the same one.

      `this.current` is cleared before the previous owner's unmount runs,
      because that unmount calls release() below. Clearing first makes that
      release a no-op on an entry that is already gone, rather than a race that
      could wipe the incoming owner a moment after it is set.
    */
    claim(token, unmount) {
      if (this.current && this.current.token !== token) {
        const previous = this.current;
        this.current = null;
        previous.unmount();
      }

      this.current = { token, unmount };
    }

    /*
      Called by an owner that has torn itself down for its own reasons -
      scrolled out of the carousel track, slide deactivated, section left the
      viewport. Guarded on identity so a stale release cannot evict a player
      that has since taken over.
    */
    release(token) {
      if (this.current && this.current.token === token) this.current = null;
    }
  }

  window.themeVideoRegistry = new VideoRegistry();
})();
